// 공개 시공사례에 무엇을 보일지 — 인메모리·Supabase 가 같이 쓰는 한 벌이고, DTO 에는 현장·고객·조직 id 를 싣지 않는다.

import type { AreaBand, Residence } from "../customer/types";
import { ORG_SETTING_DEFAULT } from "../org/types";
import { publicBodyOf, publicSummaryOf } from "./draft-sections";
import { spacePairsOf } from "./pairs";
import type {
  PortfolioImage,
  PortfolioImageStage,
  PortfolioPublicField,
  PublicPortfolioDetailRow,
  PublicPortfolioPhotoRow,
  PublicPortfolioRow,
} from "./types";

export interface PublicPortfolioImage {
  src: string;
  caption: string;
}

export interface PublicPortfolioCard {
  id: string;
  title: string;
  summary: string;
  area_band: AreaBand | null;
  residence: Residence | null;
  tags: string[];
  cover_src: string | null;
}

// 공개 링크의 대표 사례 — 공사 전/후 태그가 붙은 첫 장(없으면 null).
export interface PublicPortfolioHero extends PublicPortfolioCard {
  before_src: string | null;
  after_src: string | null;
}

// 쌍에 든 장 — index 는 detail.images 안 위치(같은 주소가 두 번 있어도 장 단위로 가른다).
export interface PublicPortfolioPairImage extends PublicPortfolioImage {
  index: number;
}

// 공간별 공사 전/후 쌍 — 공간 코드 대신 라벨만 싣는다(살아 있는 장이 전·후 둘 다 남은 공간만).
export interface PublicPortfolioPair {
  label: string;
  before: PublicPortfolioPairImage[];
  after: PublicPortfolioPairImage[];
}

export interface PublicPortfolioDetail extends PublicPortfolioCard {
  // 대표 사진의 공사 전/후 태그 — 태그 없는 장·바깥 대표면 null.
  cover_stage: PortfolioImageStage | null;
  body: string;
  images: PublicPortfolioImage[];
  pairs: PublicPortfolioPair[];
}

type PhotoRef = Pick<PublicPortfolioPhotoRow, "id" | "org_id" | "project_id">;

export const publicPortfolioPath = (slug: string): string => `/s/portfolio/${encodeURIComponent(slug)}`;

export const publicPortfolioDetailPath = (slug: string, id: string): string =>
  `${publicPortfolioPath(slug)}/${encodeURIComponent(id)}`;

export const publicPortfolioPhotoPath = (slug: string, id: string, photoId: string): string =>
  `${publicPortfolioDetailPath(slug, id)}/photos/${encodeURIComponent(photoId)}`;

const isHttpsUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname.length > 0;
  } catch {
    return false;
  }
};

const PROJECT_PHOTO_RE = /^\/app\/projects\/([^/\s?#]+)\/photos\/([^/\s?#]+)$/;

const decode = (part: string): string | null => {
  try {
    return decodeURIComponent(part);
  } catch {
    return null;
  }
};

// 대표가 현장 사진 경로면 그 사진 id — 그 사례의 현장 것일 때만.
const coverPhotoIdOf = (row: Pick<PublicPortfolioRow, "project_id" | "cover_url">): string | null => {
  const match = PROJECT_PHOTO_RE.exec(row.cover_url);
  if (!match || row.project_id === null) return null;
  return decode(match[1]) === row.project_id ? decode(match[2]) : null;
};

// 이 사례가 공개로 내줄 수 있는 현장 사진 id — 공개 행 · 현장에서 만든 사례 · 장(images)이나 대표에 든 것만.
export const publicPhotoIdsOf = (row: Pick<PublicPortfolioRow, "published" | "project_id" | "cover_url" | "images">): Set<string> => {
  const ids = new Set<string>();
  if (!row.published || row.project_id === null) return ids;
  for (const image of row.images) if (image.photo_id) ids.add(image.photo_id);
  const cover = coverPhotoIdOf(row);
  if (cover) ids.add(cover);
  return ids;
};

// 공개 행들이 가리키는 현장 사진 id 전부 — 저장소에 한 번에 물어본다.
export const referencedPublicPhotoIds = (rows: readonly PublicPortfolioRow[]): string[] => [
  ...new Set(rows.flatMap((row) => [...publicPhotoIdsOf(row)])),
];

// 사진 행이 그 사례의 조직·현장 것이고 사례가 가리키는 사진일 때만 true — 라우트가 바이트를 내주기 전에 본다.
export const isPublicPhotoOf = (
  row: Pick<PublicPortfolioRow, "org_id" | "published" | "project_id" | "cover_url" | "images">,
  photo: PhotoRef | null
): boolean =>
  photo !== null &&
  photo.org_id === row.org_id &&
  photo.project_id === row.project_id &&
  publicPhotoIdsOf(row).has(photo.id);

const liveIdsOf = (row: PublicPortfolioRow, photos: readonly PhotoRef[]): Set<string> =>
  new Set(photos.filter((p) => isPublicPhotoOf(row, p)).map((p) => p.id));

// 장 하나의 공개 주소 — 살아 있는 현장 사진은 공개 라우트로, 바깥 https 는 그대로, 그 밖은 뺀다.
const imageSrcOf = (slug: string, row: PublicPortfolioRow, image: PortfolioImage, live: ReadonlySet<string>): string | null => {
  if (image.photo_id !== undefined) return live.has(image.photo_id) ? publicPortfolioPhotoPath(slug, row.id, image.photo_id) : null;
  return isHttpsUrl(image.url) ? image.url : null;
};

const imagesOf = (slug: string, row: PublicPortfolioRow, live: ReadonlySet<string>): PublicPortfolioImage[] =>
  row.images.flatMap((image) => {
    const src = imageSrcOf(slug, row, image, live);
    return src ? [{ src, caption: image.caption }] : [];
  });

// 대표 — 살아 있는 현장 사진이면 공개 라우트, 바깥 https 면 그대로, 아니면 남은 첫 장.
const coverSrcOf = (slug: string, row: PublicPortfolioRow, live: ReadonlySet<string>, images: readonly PublicPortfolioImage[]): string | null => {
  const coverPhoto = coverPhotoIdOf(row);
  if (coverPhoto !== null && live.has(coverPhoto)) return publicPortfolioPhotoPath(slug, row.id, coverPhoto);
  if (coverPhoto === null && isHttpsUrl(row.cover_url)) return row.cover_url;
  return images[0]?.src ?? null;
};

// 끈 공개 항목은 DTO 에서부터 뺀다 — 화면이 거르지 않아도 HTML 에 안 나간다.
export const showsPublicField = (row: Pick<PublicPortfolioRow, "public_fields">, field: PortfolioPublicField): boolean =>
  row.public_fields.includes(field);

// 공개 화면의 「상담 문의」 버튼 — 조직 설정 `portfolio.public_inquiry`(기본 켬).
export const isPublicInquiryOn = (value: string | null): boolean =>
  (value ?? ORG_SETTING_DEFAULT["portfolio.public_inquiry"]) === "true";

const cardOf = (slug: string, row: PublicPortfolioRow, photos: readonly PhotoRef[]): { card: PublicPortfolioCard; images: PublicPortfolioImage[]; live: Set<string> } => {
  const live = liveIdsOf(row, photos);
  const images = imagesOf(slug, row, live);
  const card: PublicPortfolioCard = {
    id: row.id,
    title: row.title,
    summary: publicSummaryOf(row.summary, row.public_fields),
    area_band: showsPublicField(row, "area") ? row.area_band : null,
    residence: row.residence,
    tags: [...row.tags],
    cover_src: coverSrcOf(slug, row, live, images),
  };
  return { card, images, live };
};

const stageSrcOf = (slug: string, row: PublicPortfolioRow, live: ReadonlySet<string>, stage: PortfolioImageStage): string | null => {
  for (const image of row.images) {
    const src = image.stage === stage ? imageSrcOf(slug, row, image, live) : null;
    if (src) return src;
  }
  return null;
};

// 대표 사례 — 비공개·없음이면 null, 있으면 카드에 전/후 첫 장을 붙인다.
export const toPublicPortfolioHero = (
  slug: string,
  row: PublicPortfolioRow | null,
  photos: readonly PhotoRef[]
): PublicPortfolioHero | null => {
  if (!row || !row.published) return null;
  const live = liveIdsOf(row, photos);
  return {
    ...cardOf(slug, row, photos).card,
    before_src: stageSrcOf(slug, row, live, "BEFORE"),
    after_src: stageSrcOf(slug, row, live, "AFTER"),
  };
};

// 공개 행만 · sort_order(같으면 id) 순 — 목록과 B 의 대표 대신 쓸 첫 사례가 같은 순서를 쓴다.
export const publishedInOrder = <T extends Pick<PublicPortfolioRow, "id" | "published" | "sort_order">>(rows: readonly T[]): T[] =>
  rows.filter((row) => row.published).sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));

// 목록 — 공개 행만 · sort_order(같으면 id) 순.
export const toPublicPortfolioCards = (
  slug: string,
  rows: readonly PublicPortfolioRow[],
  photos: readonly PhotoRef[]
): PublicPortfolioCard[] => publishedInOrder(rows).map((row) => cardOf(slug, row, photos).card);

// 공개 주소가 남은 장만 묶는다 — 지워진 사진으로 한쪽이 비면 그 공간은 쌍에서 빠진다.
const pairsOf = (
  slug: string,
  row: PublicPortfolioRow,
  live: ReadonlySet<string>,
  spaces: readonly { code: string; label: string }[]
): PublicPortfolioPair[] => {
  const shown = row.images.flatMap((image) => {
    const src = imageSrcOf(slug, row, image, live);
    return src ? [{ src, caption: image.caption, stage: image.stage, space: image.space }] : [];
  });
  const indexed = shown.map((image, index) => ({ ...image, index }));
  const strip = ({ src, caption, index }: PublicPortfolioPairImage): PublicPortfolioPairImage => ({ src, caption, index });
  return spacePairsOf(indexed, spaces).map((g) => ({ label: g.label, before: g.before.map(strip), after: g.after.map(strip) }));
};

// 대표 주소와 같은 주소로 나가는 첫 장의 태그 — 현장 사진·바깥 https·첫 장 대체 세 경우를 한 번에 가른다.
const coverStageOf = (
  slug: string,
  row: PublicPortfolioRow,
  live: ReadonlySet<string>,
  coverSrc: string | null
): PortfolioImageStage | null => {
  if (coverSrc === null) return null;
  const image = row.images.find((item) => imageSrcOf(slug, row, item, live) === coverSrc);
  return image?.stage ?? null;
};

// 상세 — 비공개면 null · spaces 는 조직 공간 코드(순서·라벨).
export const toPublicPortfolioDetail = (
  slug: string,
  row: PublicPortfolioDetailRow | null,
  photos: readonly PhotoRef[],
  spaces: readonly { code: string; label: string }[] = []
): PublicPortfolioDetail | null => {
  if (!row || !row.published) return null;
  const { card, images, live } = cardOf(slug, row, photos);
  const pairs = pairsOf(slug, row, live, spaces);
  const cover_stage = coverStageOf(slug, row, live, card.cover_src);
  return { ...card, cover_stage, body: publicBodyOf(row.body, row.public_fields), images, pairs };
};
